import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import config from "config";

const SubscriptionContext = createContext();

/**
 * Detect if we are running inside the Android WebView with Google Play Billing available.
 */
export const isAndroidBilling = () =>
  typeof window !== "undefined" &&
  window.AndroidBridge &&
  typeof window.AndroidBridge.isBillingAvailable === "function" &&
  window.AndroidBridge.isBillingAvailable();

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingUpgrade, setPendingUpgrade] = useState(false);
  const pollTimerRef = useRef(null);
  const previousPlanRef = useRef(null);
  const billingCallbackRef = useRef(null);

  const fetchSubscription = useCallback(async (isBackground = false) => {
    try {
      // Skip on public pages (auth layout) or if no token
      if (!Cookies.get("loggedIn") || window.location.hash.startsWith("#/auth")) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }

      if (!isBackground) {
        setIsLoading(true);
      }

      const response = await axios.get(`${config.apiUrl}/mySubscription`);

      const newData = response.data;
      setSubscription(newData);
      setError(null);

      // If we were waiting for an upgrade and the plan changed, stop polling
      if (pendingUpgrade && previousPlanRef.current) {
        const newSlug = newData?.plan?.slug;
        if (newSlug && newSlug !== previousPlanRef.current) {
          setPendingUpgrade(false);
          previousPlanRef.current = null;
        }
      }
    } catch (err) {
      if (!isBackground) {
        setError(err);
      }
    } finally {
      if (!isBackground) {
        setIsLoading(false);
      }
    }
  }, [pendingUpgrade]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Auto-refresh when the browser tab regains focus (real-time feel)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (Cookies.get("loggedIn")) {
          fetchSubscription(true);
        }
      }
    };

    const handleFocus = () => {
      if (Cookies.get("loggedIn")) {
        fetchSubscription(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchSubscription]);

  // Poll for upgrade completion when user is redirected to Stripe
  useEffect(() => {
    if (pendingUpgrade) {
      // Poll every 5 seconds for up to 2 minutes to detect upgrade via webhook
      let attempts = 0;
      const maxAttempts = 24;

      pollTimerRef.current = setInterval(() => {
        attempts++;
        fetchSubscription(true);
        if (attempts >= maxAttempts) {
          setPendingUpgrade(false);
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        }
      }, 5000);

      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      };
    }
  }, [pendingUpgrade, fetchSubscription]);

  const refreshSubscription = useCallback(() => fetchSubscription(true), [fetchSubscription]);

  // Signal that user started a checkout (redirect to Stripe)
  const markPendingUpgrade = useCallback(() => {
    previousPlanRef.current = subscription?.plan?.slug || null;
    setPendingUpgrade(true);
  }, [subscription]);

  /**
   * Register the window.MufynBilling callback object so the Android native bridge
   * can notify the frontend when a purchase completes.
   * billingCallbackRef lets modals (UpgradeModal) set a handler dynamically.
   */
  useEffect(() => {
    if (!isAndroidBilling()) return;

    window.MufynBilling = {
      onPurchaseResult: (result) => {
        if (billingCallbackRef.current) {
          billingCallbackRef.current(result);
        }
      },
    };

    return () => {
      delete window.MufynBilling;
    };
  }, []);

  const setBillingCallback = useCallback((cb) => {
    billingCallbackRef.current = cb;
  }, []);

  return (
    <SubscriptionContext.Provider value={{ subscription, isLoading, error, refreshSubscription, markPendingUpgrade, pendingUpgrade, setBillingCallback }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
